import { Component, effect, inject, OnDestroy, OnInit, Renderer2, signal, ViewChild, WritableSignal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, firstValueFrom, Subscription } from 'rxjs';
import { MessageService } from '@/common/services';
import { VersionCheckService } from '@/common/services/version-check.service';
import { LayoutService } from '@/layout/services/layout.service';
import { AuthService } from '@/pages/auth/services/auth.service';
import { Offer, OfferSortField } from '@/pages/offers/models/offers.model';
import { OffersHttpService } from '@/pages/offers/services/http/offers-http.service';
import { environment } from '@environments/environment';
import { NavbarComponent } from './components/navbar/navbar.component';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
})
export class LayoutComponent implements OnInit, OnDestroy {
  private readonly _offersHttpService: OffersHttpService = inject(OffersHttpService);
  private readonly _messageService: MessageService = inject(MessageService);

  readonly hideLayout: WritableSignal<boolean> = signal<boolean>(false);
  authService = inject(AuthService);
  readonly versionCheck: VersionCheckService = inject(VersionCheckService);

  overlayMenuOpenSubscription: Subscription;

  isProduction: boolean;

  showLogout = signal(false);
  isCompanyDeletedPage = signal(false);

  menuOutsideClickListener: (() => void) | undefined;
  profileMenuOutsideClickListener: (() => void) | undefined;

  @ViewChild(NavbarComponent) appNavbar!: NavbarComponent;

  constructor(
    private _activatedRoute: ActivatedRoute,
    public layoutService: LayoutService,
    public renderer: Renderer2,
    public router: Router,
  ) {
    this.isProduction = environment.production;

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      )
      .subscribe(event => {
        const url = event.urlAfterRedirects;
        this.showLogout.set(url.includes('/company/create') || url.includes('/company/disabled') || url.includes('/company/deleted'));
        this.isCompanyDeletedPage.set(url.includes('/company/deleted'));
      });

    this.overlayMenuOpenSubscription = this.layoutService.overlayOpen$.subscribe(() => {
      if (!this.menuOutsideClickListener) {
        this.menuOutsideClickListener = this.renderer.listen('document', 'click', event => {
          const isOutsideClicked = !(this.appNavbar.menuButton.nativeElement.isSameNode(event.target) || this.appNavbar.menuButton.nativeElement.contains(event.target));

          if (isOutsideClicked) {
            this.hideMenu();
          }
        });
      }

      if (!this.profileMenuOutsideClickListener) {
        this.profileMenuOutsideClickListener = this.renderer.listen('document', 'click', event => {
          const isOutsideClicked = !(this.appNavbar.menu.nativeElement.isSameNode(event.target)
            || this.appNavbar.menu.nativeElement.contains(event.target)
            || this.appNavbar.navbarMenuButton.nativeElement.isSameNode(event.target)
            || this.appNavbar.navbarMenuButton.nativeElement.contains(event.target));

          if (isOutsideClicked) {
            this.hideProfileMenu();
          }
        });
      }

      if (this.layoutService.state.staticMenuMobileActive) {
        this.blockBodyScroll();
      }
    });

    this.router.events.pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.hideMenu();
        this.hideProfileMenu();
      });

    // Cuando el welcome dialog se cierra, mostrar el tutorial si procede
    // Variable de clausura para rastrear la transición visible→no visible
    let welcomeWasVisible = false;
    effect(() => {
      const welcomeVisible = this.layoutService.showWelcomeDialog();

      if (!welcomeVisible && welcomeWasVisible) {
        welcomeWasVisible = false;
        this.layoutService.checkAndShowTutorialDialog(this.hideLayout());
      }

      if (welcomeVisible) {
        welcomeWasVisible = true;
      }
    }, { allowSignalWrites: true });
  }

  ngOnInit(): void {
    this.versionCheck.startPolling();
    // Leer query param del callback de Google Calendar
    this._activatedRoute.queryParams.pipe(filter(params => params['calendar'])).subscribe(params => {
      const status = params['calendar'];
      if (status === 'connected') {
        this._messageService.add({
          severity: 'success',
          summary: 'COMMON.SUCCESS.TITLE',
          detail: 'DATA_MANAGEMENT.CALENDAR_CONNECTED',
        });
      } else if (status === 'error') {
        this._messageService.add({
          severity: 'error',
          summary: 'COMMON.ERRORS.TITLE',
          detail: 'DATA_MANAGEMENT.CALENDAR_ERROR',
        });
      }
      // Limpiar query param de la URL
      this.router.navigate([], {
        queryParams: { calendar: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    });

    // Accessing the route's data on any child
    this.router.events.subscribe(() => {
      this.hideLayout.set(this.computeHideLayout());
    });

    // solo mostrar el dialogo de bienvenida si la empresa esatá activa

    // Mostrar advertencia de móvil si el usuario está en un dispositivo móvil (no aplica en rutas con hideLayout, ej. crear empresa)
    const isMobileWarningShown = this.layoutService.checkAndShowMobileWarningDialog(this.computeHideLayout());

    // Check if user hasn't opted out of welcome dialog
    // Solo mostrar si no se mostró el mobile warning (ese lo mostrará después de cerrarse)
    if (!isMobileWarningShown && !(this.authService.getUser()?.entity.id === null || this.authService.getUser()?.entity.createProcess)) {
      this.layoutService.checkAndShowWelcomeDialog(this.hideLayout());
    }

    // Tutorial guiado: mostrar solo si el welcome dialog NO está visible
    // Si el welcome está visible, el effect() del constructor se encarga de mostrarlo al cerrarse
    if (!isMobileWarningShown && !this.layoutService.showWelcomeDialog()) {
      this.layoutService.checkAndShowTutorialDialog(this.hideLayout());
    }
  }

  get containerClass(): object {
    return {
      /* eslint-disable @typescript-eslint/naming-convention */
      'layout-theme-light': this.layoutService.config().colorScheme === 'light',
      'layout-theme-dark': this.layoutService.config().colorScheme === 'dark',
      'layout-overlay': this.layoutService.config().menuMode === 'overlay',
      'layout-static': this.layoutService.config().menuMode === 'static',
      'layout-static-inactive': this.layoutService.state.staticMenuDesktopInactive && this.layoutService.config().menuMode === 'static',
      'layout-overlay-active': this.layoutService.state.overlayMenuActive,
      'layout-mobile-active': this.layoutService.state.staticMenuMobileActive,
      'p-input-filled': this.layoutService.config().inputStyle === 'filled',
      'p-ripple-disabled': !this.layoutService.config().ripple,
    };
  }

  hideMenu(): void {
    this.layoutService.state.overlayMenuActive = false;
    this.layoutService.state.staticMenuMobileActive = false;
    this.layoutService.state.menuHoverActive = false;
    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
      this.menuOutsideClickListener = undefined;
    }
    this.unblockBodyScroll();
  }

  hideProfileMenu(): void {
    this.layoutService.state.profileSidebarVisible = false;
    if (this.profileMenuOutsideClickListener) {
      this.profileMenuOutsideClickListener();
      this.profileMenuOutsideClickListener = undefined;
    }
  }

  blockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.add('blocked-scroll');
    } else {
      document.body.className += ' blocked-scroll';
    }
  }

  unblockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.remove('blocked-scroll');
    } else {
      document.body.className = document.body.className.replace(new RegExp('(^|\\b)' +
        'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
    }
  }

  onTourFinishedClosed(): void {
    this.layoutService.tourFinishedShowCandidatesHint.set(false);

    const pendingCandidatesOfferId = this.layoutService.pendingCandidatesTourOfferId();

    if (pendingCandidatesOfferId) {
      // Viene del tour de ofertas con candidatos pendientes → lanzar tour de candidatos
      this.layoutService.pendingCandidatesTourOfferId.set(null);
      // Encadenado: la guía de Candidat@s se abre justo después de terminar la de Ofertas
      this.layoutService.chainedTourActive.set(true);
      localStorage.setItem('preferredView', 'list');
      this.router.navigate(['/offers', pendingCandidatesOfferId]).then(() => {
        setTimeout(() => {
          this.layoutService.startCandidatesTourRequested.set(true);
        }, 500);
      });
    } else if (this.layoutService.pendingOffersTourAfterFinished()) {
      this.layoutService.pendingOffersTourAfterFinished.set(false);
      if (!this.layoutService.isOffersListTourDismissed()) {
        // Encadenado: la guía de Ofertas se abre justo después de terminar la General
        this.layoutService.chainedTourActive.set(true);
        this.layoutService.showOffersTutorialDialog.set(true);
      } else {
        this.layoutService.tourInProgress.set(false);
        this.layoutService.firstOfferModalAllowed.set(true);
        this.layoutService.showViewPickerDialogRequested.set(true);
      }
    } else {
      // Fin de toda la secuencia de tours → solicitar view picker si aplica
      this.layoutService.tourInProgress.set(false);
      this.layoutService.firstOfferModalAllowed.set(true);
      this.layoutService.showViewPickerDialogRequested.set(true);
    }
  }

  onOffersTutorialClosed(): void {
    // Si venimos de la cascada de login (Guía General -> Guía Ofertas) y el usuario cierra/descarta
    // el diálogo de Ofertas sin iniciar el tour, seguir encadenando hacia la Guía Candidat@s si procede.
    if (this.layoutService.loginCascadeActive()) {
      this.layoutService.loginCascadeActive.set(false);
      this.cascadeToCandidatesTourIfAny();

      return;
    }

    // Si el usuario cierra el dialog sin iniciar el tour, resetear tourInProgress
    this.layoutService.tourInProgress.set(false);
    this.layoutService.firstOfferModalAllowed.set(true);
  }

  ngOnDestroy(): void {
    this.versionCheck.stopPolling();
    if (this.overlayMenuOpenSubscription) {
      this.overlayMenuOpenSubscription.unsubscribe();
    }

    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
    }
  }

  logout(): void {
    this.authService.logout();
  }

  onLogoClick(event: Event): void {
    // En /company/deleted la sesión sigue activa y el login redirigiría de vuelta aquí (bucle), así que se cierra sesión
    if (this.isCompanyDeletedPage()) {
      event.preventDefault();
      this.logout();
    }
  }

  onMobileWarningClosed(): void {
    // Mostrar el welcome dialog después de cerrar el mobile warning
    if (!(this.authService.getUser()?.entity.id === null || this.authService.getUser()?.entity.createProcess)) {
      this.layoutService.checkAndShowWelcomeDialog(this.hideLayout());
    }

    // Si el welcome no se va a mostrar, mostrar el tutorial directamente
    if (!this.layoutService.showWelcomeDialog()) {
      this.layoutService.checkAndShowTutorialDialog(this.hideLayout());
    }
  }

  private computeHideLayout(): boolean {
    // Traverse the route tree to find the correct child with the data
    let childRoute = this._activatedRoute.firstChild;

    while (childRoute) {
      if (childRoute.snapshot.data['hideLayout'] !== undefined) {
        return true;
      }
      childRoute = childRoute.firstChild;
    }

    return false;
  }

  private cascadeToCandidatesTourIfAny(): void {
    firstValueFrom(this._offersHttpService.fetch({ limit: 50, offset: 0, sortField: OfferSortField.DATE, sortOrder: 'desc' })).then(response => {
      const offerWithCandidates = response.results.find((offer: Offer) => offer.candidates.TOTAL > 0);
      if (offerWithCandidates) {
        localStorage.setItem('preferredView', 'list');
        this.router.navigate(['/offers', offerWithCandidates.id]).then(() => {
          setTimeout(() => {
            this.layoutService.startCandidatesTourRequested.set(true);
          }, 500);
        });
      } else {
        this.layoutService.tourInProgress.set(false);
        this.layoutService.firstOfferModalAllowed.set(true);
      }
    }).catch(() => {
      this.layoutService.tourInProgress.set(false);
      this.layoutService.firstOfferModalAllowed.set(true);
    });
  }
}
